import { Fetcher } from '../Fetching/Fetcher';
import { GET_TREE } from '../../queries/Tree';
import { ITree, Tree } from './Tree';
import { Instance, SnapshotIn, SnapshotOut, applySnapshot, cast, flow, types } from 'mobx-state-tree';
import { captureException } from '../../../utils/sentry/sentry';

// TODO move to backend
const disableStepBackFor = ['appointmentCreated', 'appointmentError'];

export const TreeStore = Fetcher
  .named('TreeStore')
  .props({
    trees: types.optional(types.array(Tree), []),
  })
  .volatile(self => ({
    staleTime: 60 * 60 * 1000, // override default stale time to 1 hour
  }))
  .actions(self => {
    const fetchTree = flow(function*(dataId: string, dataType: string) {
      self.setFetchPending(dataId);
      console.log('TreeStore.fetchTree', dataId, dataType);
      yield self.fetchData(dataId, async () => {
        const response = await self.client.query({
          query: GET_TREE,
          // the tree is content: a session entered again must see the steps
          // as they are now, not as Apollo cached them earlier in the run
          fetchPolicy: 'network-only',
          variables: {
            dataId,
            dataType,
          }
        });
        if (response.errors) {
          throw new Error(response.errors[0].message);
        } else {
          return response.data.getTree;
        }
      });
    });
    const loadFromFetch = function(dataId: string) {
      console.log('TreeStore.loadTree', dataId);
      self.loadFetchedData(Tree, dataId, partial => {
        // mobx identifiers must be unique. here we apply the tree namespace
        // to node identifiers to avoid conflicts. edges references must also
        // be updated to match the identifiers.
        // two nodes with the same key get the same identifier: resolving an
        // edge reference then throws ("multiple candidates"). Fail this
        // session (the fetch error shows its error screen) before the tree
        // reaches the persisted store, where every later session reads it.
        const nodes = partial?.nodes || [];
        const keys = nodes.map(node => `${node.key}`);
        const duplicates = [...new Set(keys.filter((key, i) => keys.indexOf(key) !== i))];
        if (duplicates.length) {
          const error = new Error(`Tree ${dataId}: several nodes with the key ${duplicates.join(', ')}`);
          captureException(error);
          throw error;
        }
        const nodeKeys = new Set(nodes.map(n => `${n.key}`));
        const filteredEdges = (partial?.edges || []).filter(edge => {
          if (nodeKeys.has(`${edge.previous}`) && nodeKeys.has(`${edge.next}`)) {
            return true;
          }
          // Drop orphan edges (target/source node missing). Prevents MST
          // reference-resolution crashes when a stale cached tree still
          // references a node removed or not-yet-migrated on the server.
          console.warn(`Dropping orphan tree edge ${edge.previous}->${edge.next} (missing node)`);
          return false;
        });
        const tree = {
          ...partial,
          edges: filteredEdges.map(edge => ({
            ...edge,
            previousKey: edge.previous,
            nextKey: edge.next,
            previous: `${partial.namespace || ''}.${edge.previous}`,
            next: `${partial.namespace || ''}.${edge.next}`,
          })),
          nodes: nodes.map(node => ({
            ...node,
            id: `${node.namespace || ''}.${node.key}`,
            disableStepBack: disableStepBackFor.includes(node.key),
          })),
        } as SnapshotIn<ITree>;
        const index = self.trees.findIndex(p => p.dataRef.dataId === dataId);
        if (index === -1) {
          self.trees.push(tree);
        } else {
          // merge in place instead of replacing the node: a replacement
          // detaches the old Tree while live observers (the session screen,
          // the overview) still hold it, spraying "no longer part of a state
          // tree" warnings on every subsequent read (same class of bug as the
          // Activity store's day models)
          applySnapshot(self.trees[index], tree);
        }
      });
    };
    const _loadTree = flow(function*(dataId: string, dataType: string, force = false) {
      if (force || !self.isFetchDone(dataId)) {
        yield fetchTree(dataId, dataType);
      }
      if (self.hasFetchError(dataId)) {
        return;
      }
      return loadFromFetch(dataId);
    });
    return {
      clear() {
        self.trees = cast([]);
        self.clearFetchStates();
      },
      removeTree(dataId: string) {
        const index = self.trees.findIndex(p => p.dataRef.dataId === dataId);
        // findIndex miss used to fall through to splice(-1, 1), silently
        // removing the LAST tree instead of the requested one
        if (index !== -1) {
          self.trees.splice(index, 1);
        }
        self.clearFetchState(dataId);
      },
      fetchTree,
      /** force: refetch even a fresh (< staleTime) tree — the persisted store
       *  can hold a tree from a previous run whose steps changed since */
      loadTree: (dataId: string, dataType: string, force = false) => {
        const state = self.states.find(s => s.id === dataId);
        console.log('TreeStore.loadTree', dataId, state?.state, JSON.stringify(state));
        if (!dataId) {
          return Promise.resolve();
        }
        if (!force && !self.shouldRefetch(dataId)) {
          return Promise.resolve();
        }
        return self._deduplicate(dataId, () => _loadTree(dataId, dataType, force));
      },
      reloadTree: flow(function*(dataId: string, dataType: string) {
        console.log('TreeStore.reloadTree', dataId);
        yield fetchTree(dataId, dataType);
        return loadFromFetch(dataId);
      }),
    };
  })
  .views(self => ({
    hasTree(dataId: string) {
      const fetched = self.isFetchDone(dataId);
      const loaded = self.trees.some(p => p.dataRef.dataId === dataId);
      return fetched || loaded;
    },
    getTree(dataId: string) {
      const loading = self.isFetchPending(dataId);
      const error = self.getFetchError(dataId);
      const data = self.trees.find(p => p.dataRef.dataId === dataId);
      return {data, loading, error};
    },
  }));

export type ITreeStore = Instance<typeof TreeStore>;
export type ITreeStoreSnapshotIn = SnapshotIn<ITreeStore>;
export type ITreeStoreSnapshotOut = SnapshotOut<ITreeStore>
